import React from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { colors } from "../../styles/style";
import { FeatureKey, FEATURE_LABELS, canUseFeature } from "../../common/plan";
import { useBabyContext } from "../../context/BabyContext";

interface ProGateProps {
  feature: FeatureKey;
  children: React.ReactNode;
}

/**
 * Wraps a subscriber-only page. A paying family sees it untouched; everyone
 * else sees it too, under a banner saying adding to it needs Pro.
 *
 * Read-only rather than hidden: families who filled these books in before the
 * free plan existed keep seeing what they made. Only adding and editing needs
 * Pro, and the server refuses those writes whatever the page shows.
 */
export const ProGate: React.FC<ProGateProps> = ({ feature, children }) => {
  const { babyHasActivePayment, isBabyAdmin, babyAdminName } = useBabyContext();
  const navigate = useNavigate();

  if (canUseFeature(feature, babyHasActivePayment)) {
    return <>{children}</>;
  }

  const label = FEATURE_LABELS[feature];
  /* Only the baby's admin can buy the subscription that unlocks it. Everyone
     else is told who to ask — a Subscribe button would sell them something
     that leaves this page exactly as locked as it is now. */
  const canBuy = isBabyAdmin !== false;

  return (
    <GateRoot>
      <Banner role="status" aria-label={`${label} is part of Pro`}>
        <LockMark aria-hidden="true">
          <LockBody />
          <LockShackle />
        </LockMark>
        <BannerText>
          <BannerTitle>{label} is part of Pro</BannerTitle>
          <BannerBody>
            {canBuy
              ? `Everything already here stays yours. Upgrade to add more.`
              : babyAdminName
                ? `${babyAdminName} can upgrade this baby to Pro so everyone can add to it.`
                : `Whoever set this baby up can upgrade it to Pro so everyone can add to it.`}
          </BannerBody>
        </BannerText>
        {canBuy && (
          <SubscribeButton type="button" onClick={() => navigate("/settings")}>
            See Pro
          </SubscribeButton>
        )}
      </Banner>
      {/* Left fully usable: Back buttons, photo viewers and detail pages all
          live inside the page. Saving is what the server refuses, and
          ProRequiredNotice explains it when it does. */}
      {children}
    </GateRoot>
  );
};

const GateRoot = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const Banner = styled.div`
  position: sticky;
  top: env(safe-area-inset-top, 0px);
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 8px 16px;
  padding: 12px 14px;
  border-radius: 20px;
  background-color: ${colors.white};
  box-shadow: 0 4px 20px ${colors.warmthGrey};
`;

const BannerText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const LockMark = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background-color: ${colors.softPink};
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 12px;
`;

const LockBody = styled.span`
  width: 18px;
  height: 13px;
  border-radius: 3px;
  background-color: ${colors.warmthBrown};
`;

const LockShackle = styled.span`
  position: absolute;
  top: 11px;
  width: 11px;
  height: 9px;
  border: 2px solid ${colors.warmthBrown};
  border-bottom: none;
  border-radius: 6px 6px 0 0;
`;

const BannerTitle = styled.span`
  color: ${colors.darkBrown};
  font-size: 15px;
  font-weight: 500;
`;

const BannerBody = styled.span`
  color: ${colors.warmthBrown};
  font-size: 13px;
  line-height: 18px;
`;

const SubscribeButton = styled.button`
  flex-shrink: 0;
  padding: 10px 16px;
  border: none;
  border-radius: 39px;
  background-color: ${colors.softBrown};
  color: ${colors.white};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
`;
